import React, { useState } from 'react'
import { match } from 'ts-pattern'
import { useTranslation } from 'react-i18next'
import { Button, CardActions, CardHeader, Stack, Tab, Tabs } from '@mui/material'
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import ItemDetail from './shared/ItemDetail'
import MenuItemOverview from './Overview'
import MenuItemIngredients from './Ingredients'
import MenuItemSourcing from './Sourcing'
import MenuItemNutritional from './Nutritional'
import QuantitySelect from '@/components/QuantitySelect'
import { CartMappingOptionInput } from '../MenuBody/cartMapping'
import { optionsAreValid } from '../ccMenuSpecificHelpers'
import { numberFormat } from '@/helpers/numbers'

const canteenMenuItemDetailUserDataFragment = graphql(`
  fragment CanteenMenuItemDetail_UserDataFragment on User {
    account {
      userInterfaceSettings {
        hideCCPrices
      }
    }
  }
`)

const canteenMenuItemDetailItemDataFragment = graphql(`
  fragment CanteenMenuItemDetail_ItemDataFragment on MenuItem {
    ...CanteenMenuItemOverview_ItemDataFragment
    ...CanteenMenuItemIngredients_ItemDataFragment
    ...CanteenMenuItemNutritional_ItemDataFragment
    _id
    name
    image
    variations {
      _id
      priceCustomerFacing
    }
    options {
      _id
      minQty
      maxQty
      qtyPerPerson
    }
  }
`)

const canteenMenuItemDetailVendorDataFragment = graphql(`
  fragment CanteenMenuItemDetail_VendorDataFragment on Vendor {
    ...CanteenMenuItemIngredients_VendorDataFragment
    ...CanteenMenuItemSourcing_VendorDataFragment
    ...CanteenMenuItemNutritional_VendorDataFragment
    id
    companyName
    logo
  }
`)

type Props = {
  selectedVariationId: string
  itemData: FragmentType<typeof canteenMenuItemDetailItemDataFragment>
  vendorData: FragmentType<typeof canteenMenuItemDetailVendorDataFragment>
  userData: FragmentType<typeof canteenMenuItemDetailUserDataFragment>
  onAddToCart: (quantity: number, options: Array<CartMappingOptionInput>) => void
}

const MenuItemDetail: React.FC<Props> = ({
  selectedVariationId,
  itemData: itemDataProp,
  vendorData: vendorDataProp,
  userData: userDataProp,
  onAddToCart,
}) => {
  const itemData = useFragment(canteenMenuItemDetailItemDataFragment, itemDataProp)
  const { name, image, variations, options } = itemData

  const vendorData = useFragment(canteenMenuItemDetailVendorDataFragment, vendorDataProp)
  const { companyName, logo } = vendorData

  const userData = useFragment(canteenMenuItemDetailUserDataFragment, userDataProp)
  const hidePrices = Boolean(userData.account?.userInterfaceSettings.hideCCPrices)

  const { t } = useTranslation()
  const [selectedTab, setSelectedTab] = useState(0)
  const [quantity, setQuantity] = useState(1)
  const [optionsState, setOptionsState] = useState<
    Record<string, Record<string, { qty: number; price: number; name: string }>>
  >({})

  const setOption = (
    optionData: {
      categoryId: string
      optionId: string
      price: number
      name: string
    },
    optionQuantity: number,
  ) => {
    setOptionsState((prev) => {
      const newOptionState = {
        ...prev,
        [optionData.categoryId]: {
          ...prev[optionData.categoryId],
          [optionData.optionId]: {
            price: optionData.price,
            qty: optionQuantity,
            name: optionData.name,
          },
        },
      }
      if (optionQuantity === 0) {
        delete newOptionState[optionData.categoryId][optionData.optionId]
      }
      return newOptionState
    })
  }

  const handleAddToCart = () => {
    const optionsToAdd = Object.entries(optionsState).flatMap((optionCategory) => {
      const [categoryId, optionItems] = optionCategory
      return Object.entries(optionItems).map((optionItem) => {
        const [itemId, optionItemData] = optionItem
        const returnOption: CartMappingOptionInput = {
          item: {
            id: itemId,
            name: optionItemData.name,
            price: optionItemData.price,
          },
          category: { id: categoryId },
        }
        return returnOption
      })
    })
    onAddToCart(quantity, optionsToAdd)
  }

  const ccVariation = variations?.find((variation) => variation._id === selectedVariationId)
  if (!ccVariation) throw new Error('Something wrong with this item')

  const validOptions = options ? optionsAreValid(options, optionsState) : true
  const selectedOptionsTotal = Object.values(optionsState).reduce((total, optionCategory) => {
    return Object.values(optionCategory).reduce((categoryTotal, option) => {
      return categoryTotal + option.price * option.qty
    }, total)
  }, 0)

  const itemTotal = (ccVariation.priceCustomerFacing || 0) + selectedOptionsTotal
  const itemQuantityTotal = itemTotal * quantity

  return (
    <ItemDetail
      itemName={name || ''}
      vendorName={companyName}
      vendorLogo={logo || ''}
      itemImage={image || ''}
    >
      <Stack direction="column" position="sticky" top={0} bgcolor="background.paper" zIndex={3}>
        <Tabs
          value={selectedTab}
          onChange={(_, value) => setSelectedTab(value)}
          aria-label="item-detail-tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value={0} label={t('cc:overview')} />
          <Tab value={1} label={t('cc:ingredients')} />
          <Tab value={2} label={t('cc:nutritional')} />
          <Tab value={3} label={t('cc:sourcing')} />
        </Tabs>
        <CardHeader title={name} />
      </Stack>
      <Stack direction="column" justifyContent="space-between" height="100%">
        {match(selectedTab)
          .with(0, () => (
            <MenuItemOverview
              ccVariationId={ccVariation._id}
              selectedOptions={optionsState}
              onOptionSelection={setOption}
              itemData={itemData}
            />
          ))
          .with(1, () => <MenuItemIngredients itemData={itemData} vendorData={vendorData} />)
          .with(2, () => <MenuItemNutritional itemData={itemData} vendorData={vendorData} />)
          .with(3, () => <MenuItemSourcing vendorData={vendorData} />)
          .otherwise(() => (
            <></>
          ))}
        <CardActions
          sx={{
            backgroundColor: 'background.paper',
            position: 'sticky',
            bottom: 0,
          }}
        >
          <Stack width="100%" direction="row">
            <QuantitySelect min={1} quantity={quantity} onChange={(value) => setQuantity(value)} />
            <Button
              fullWidth
              variant="contained"
              onClick={() => handleAddToCart()}
              disabled={!validOptions}
              data-cy="canteen-menu-item-add-to-cart"
            >
              {validOptions ? (
                <>
                  {t('cc:addToCart', {
                    count: quantity,
                  })}{' '}
                  {!hidePrices ? <>({numberFormat(itemQuantityTotal / 100)})</> : null}
                </>
              ) : (
                <>Choose options to add to cart</>
              )}
            </Button>
          </Stack>
        </CardActions>
      </Stack>
    </ItemDetail>
  )
}

export default MenuItemDetail
